'use client';

import { type KeyboardEvent, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CalendarView } from './UiDatePicker.types';
import { type DateRange, calendarHead, clampToRange, isInRange, todayIso, trapTab } from './UiDatePicker.utils';
import { UiDatePickerDays } from './UiDatePickerDays';
import { UiDatePickerFoot } from './UiDatePickerFoot';
import { UiDatePickerHead } from './UiDatePickerHead';
import { UiDatePickerMonths } from './UiDatePickerMonths';
import { UiDatePickerYears } from './UiDatePickerYears';
import { usePopoverPosition } from './usePopoverPosition';

type UiDatePickerPopoverProps = {
    id: string;
    label: string;
    value: string;
    range: DateRange;
    anchorRef: RefObject<HTMLElement | null>;
    /** Opened from the keyboard or the button: the focus moves into the grid. */
    autoFocus: boolean;
    onSelect: (iso: string) => void;
    onClear: () => void;
    onClose: () => void;
};

/**
 * The calendar: days, and on the title months and years to jump with. It opens
 * in the top layer, so no scroll area or dialog around the field clips it.
 */
export function UiDatePickerPopover({
    id,
    label,
    value,
    range,
    anchorRef,
    autoFocus,
    onSelect,
    onClear,
    onClose,
}: UiDatePickerPopoverProps) {
    const popoverRef = useRef<HTMLDivElement>(null);
    const [view, setView] = useState<CalendarView>('days');
    const [focus, setFocus] = useState(() => clampToRange(value || todayIso(), range));
    const moveFocusRef = useRef(autoFocus);

    usePopoverPosition(anchorRef, popoverRef);

    useLayoutEffect(() => {
        const popover = popoverRef.current;
        if (popover?.showPopover && !popover.matches(':popover-open')) popover.showPopover();
    }, []);

    // After a keyboard move or a change of view the focus follows the grid's focused cell.
    useEffect(() => {
        if (!moveFocusRef.current) return;

        moveFocusRef.current = false;
        popoverRef.current?.querySelector<HTMLElement>('[data-focused]')?.focus();
    });

    const changeFocus = (iso: string, moveFocus = false) => {
        moveFocusRef.current = moveFocus;
        setFocus(iso);
    };

    const changeView = (next: CalendarView, iso = focus) => {
        moveFocusRef.current = true;
        setFocus(iso);
        setView(next);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab') trapTab(event, popoverRef.current);
        if (event.key !== 'Escape') return;

        // Prevented, so a dialog around the field stays open (Headless UI skips prevented events).
        event.preventDefault();
        event.stopPropagation();

        if (view === 'days') onClose();
        else changeView('days');
    };

    const head = calendarHead(view, focus, range);
    const gridProps = { focus, value, range, onFocusChange: changeFocus };

    return (
        <div
            ref={popoverRef}
            id={id}
            popover="manual"
            role="dialog"
            aria-label={label}
            className="form-datepicker"
            onKeyDown={handleKeyDown}
        >
            <UiDatePickerHead
                head={head}
                onStep={(iso) => changeFocus(iso)}
                onUp={() => head.up && changeView(head.up.view)}
            />

            {view === 'days' ? <UiDatePickerDays {...gridProps} label={head.title} onSelect={onSelect} /> : null}
            {view === 'months' ? (
                <UiDatePickerMonths {...gridProps} onSelect={(iso) => changeView('days', iso)} />
            ) : null}
            {view === 'years' ? (
                <UiDatePickerYears {...gridProps} onSelect={(iso) => changeView('months', iso)} />
            ) : null}

            <UiDatePickerFoot
                todayDisabled={!isInRange(todayIso(), range)}
                onClear={onClear}
                onToday={() => onSelect(todayIso())}
            />
        </div>
    );
}
